import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { Storage } from '@angular/fire/storage';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import Uppy from '@uppy/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AccountingService } from '../../../services/accounting.service';
import { CommonModule } from '@angular/common';

import {
  getDownloadURL,
  ref,
  uploadBytesResumable,
} from '@angular/fire/storage';
import {
  UppyAngularDashboardModalModule,
  UppyAngularDashboardModule,
  UppyAngularDragDropModule,
  UppyAngularProgressBarModule,
  UppyAngularStatusBarModule,
} from '@uppy/angular';
import { ToastrService } from 'ngx-toastr';
import { UserModel } from '../../../models/user.model';
import { InitialsPipe } from '../../../pipes/initials.pipe';
import { MomentPipe } from '../../../pipes/moment.pipe';
import { UtilsService } from '../../../services/utils.service';
import * as userActions from '../../../state/actions/user.actions';
import { AppState } from '../../../state/appstate';
import { BtnActionComponent } from '../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../components/buttons/btn-back/btn-back.component';
import { BtnRouteComponent } from '../../components/buttons/btn-route/btn-route.component';
import { ViewLocationComponent } from '../../components/maps/view-location/view-location.component';

@Component({
  selector: 'app-view-profile',
  standalone: true,
  templateUrl: './view-profile.component.html',
  styleUrls: ['./view-profile.component.css'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    BtnBackComponent,
    BtnActionComponent,
    ViewLocationComponent,
    UppyAngularDashboardModalModule,
    UppyAngularDashboardModule,
    UppyAngularDragDropModule,
    UppyAngularProgressBarModule,
    UppyAngularStatusBarModule,
    MomentPipe,
    BtnRouteComponent,
    InitialsPipe,
  ],
})
export class ViewProfileComponent implements OnInit, OnDestroy {
  private readonly storage: Storage = inject(Storage);
  private readonly destroy$ = new Subject<void>();

  pageForm: FormGroup;

  user: UserModel | null = null;

  progress: string | null = null;

  uppy: Uppy = new Uppy({
    restrictions: {
      maxFileSize: 5242880, // 5 MB
      maxNumberOfFiles: 1,
      minNumberOfFiles: 1,
      allowedFileTypes: ['.jpg', '.jpeg', '.png'],
    },
  });

  showModal: boolean = false;
  dashboardModalProps = {
    target: document.body,
    onRequestCloseModal: (): void => {
      this.showModal = false;
    },
  };

  utils = UtilsService;

  constructor(
    private store: Store<AppState>,
    private toastr: ToastrService,
  ) {
    this.store.select('user').pipe(takeUntil(this.destroy$)).subscribe((state) => {
      this.user = state.user;
    });
    this.pageForm = new FormGroup({
      name: new FormControl('', Validators.required),
      email: new FormControl('', Validators.required),
      password: new FormControl('', Validators.required),
    });
  }

  uploadFile(event: any) {
    if (!event.target.files) return;

    const file: File = event.target.files[0];

    if (file == null) return;

    const storageRef = ref(
      this.storage,
      `users/${this.user?.uid}/${file.name}`,
    );

    const uploadTask = uploadBytesResumable(storageRef, file);

    uploadTask.on(
      'state_changed',
      (snapshot) => {
        // Observe state change events such as progress, pause, and resume
        // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
        const progress =
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        this.progress = `Upload: ${progress.toFixed(2)}%`;
        switch (snapshot.state) {
          case 'paused':
            // console.log('Upload is paused');
            break;
          case 'running':
            // console.log('Upload is running');
            break;
        }
      },
      (error) => {
        this.toastr.error(
          `Error occurred while uploading the profile image. Please try again later.\n${error}`,
          'Error Uploading Image',
        );
        this.progress = null;
      },
      () => {
        this.toastr.success(
          'Profile image uploaded successfully. The change will reflect in some time.',
          'Success',
        );
        getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
          this.store.dispatch(
            userActions.loadUserSuccess({
              user: { ...this.user, photoURL: downloadURL },
            }),
          );
        });
        this.progress = null;
      },
    );
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  update() {
    if (this.pageForm.invalid) return;
    this.store.dispatch(userActions.update(this.pageForm.value));
  }

  refresh() {
    if (this.user?.uid == null) return;
    this.store.dispatch(userActions.getUser({ uid: this.user.uid }));
  }

  get firstName(): string {
    if (!this.user?.name) return '';
    const parts = this.user.name.trim().split(/\s+/);
    return parts[0] || '';
  }

  get lastName(): string {
    if (!this.user?.name) return '';
    const parts = this.user.name.trim().split(/\s+/);
    return parts.slice(1).join(' ') || '';
  }

  deleteProfileImage() {
    if (this.user == null) return;
    this.store.dispatch(userActions.deleteImage({ user: this.user! }));
  }
}
